import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../button";
import styles from "./style.module.css";

interface RenameDeviceModalProps {
  open: boolean;
  initialName: string;
  onClose: () => void;
  onConfirm: (name: string) => void;
  loading?: boolean;
}

/**
 * Переименование устройства. По аналогии с delete-device-modal:
 * bottom sheet на мобилке, центр экрана на ≥768px.
 */
function RenameDeviceModal({
  open,
  initialName,
  onClose,
  onConfirm,
  loading = false
}: RenameDeviceModalProps) {
  const [animatedOpen, setAnimatedOpen] = useState(false);
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (!open) {
      setAnimatedOpen(false);
      return;
    }
    setName(initialName);
    const frame = requestAnimationFrame(() => setAnimatedOpen(true));
    return () => cancelAnimationFrame(frame);
  }, [open, initialName]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== initialName.trim();

  return createPortal(
    <div
      className={`${styles.overlay} ${animatedOpen ? styles.open : ""}`}
      role="presentation"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-device-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 id="rename-device-modal-title" className={styles.title}>
            Переименовать устройство
          </h2>
          <p className={styles.subtitle}>Имя видно только вам в списке устройств</p>
        </div>
        <input
          className={styles.input}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 64))}
          placeholder="Название устройства"
          maxLength={64}
          disabled={loading}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit && !loading) {
              e.preventDefault();
              onConfirm(trimmed);
            }
          }}
        />
        <div className={styles.actions}>
          <Button
            label="Сохранить"
            onClick={() => onConfirm(trimmed)}
            disabled={!canSubmit || loading}
            loading={loading}
          />
          <Button label="Отмена" variant="secondary" onClick={onClose} disabled={loading} />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RenameDeviceModal;
